import { type PropsWithChildren, type ReactElement } from 'react'
import { useHotKey, useIsMac } from '@hooks'
import { getName, type UserId, type UserProfileData } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { AppShell, Group, Stack, Text } from '@mantine/core'
import { SizePrimitives, useBackend, useTranslation } from '@front/model'
import { TButton } from '../buttons'
import { TerrosHeaderLogo } from './TerrosLogo'

type Props = PropsWithChildren & {
  user?: UserProfileData | undefined
  refetch?: VoidFunction
}

export const useHeaderHeight = (): number => {
  const { getImpersonating } = useBackend()
  const impersonating = getImpersonating()

  return impersonating ? 100 : 60
}

export const Header = ({ children, user, refetch }: Props): ReactElement => {
  const { getImpersonating } = useBackend()
  const impersonating = getImpersonating()

  return (
    <AppShell.Header className='flex rounded-b-xl pb-0' bg='blue'>
      <Stack flex={1} p={0} gap={0}>
        {children}
        {impersonating && <ImpersonationBanner refetch={refetch} impersonating={impersonating} user={user} />}
      </Stack>
    </AppShell.Header>
  )
}

const ImpersonationBanner = ({
  impersonating,
  refetch,
  user,
}: {
  impersonating: UserId
  refetch: VoidFunction | undefined
  user: UserProfileData | undefined
}): ReactElement => {
  const { setImpersonating } = useBackend()
  const { t } = useTranslation()
  const isMac = useIsMac()

  const queryClient = useQueryClient()
  const stopImpersonating = (): void => {
    setImpersonating()
    queryClient.resetQueries()
    setTimeout(() => refetch?.(), 500)
  }
  useHotKey(
    (event) =>
      (isMac ? event.metaKey : event.ctrlKey) &&
      event.shiftKey === isMac &&
      event.altKey !== isMac &&
      !event.repeat &&
      !event.isComposing &&
      event.key.toLowerCase() === 'i',
    stopImpersonating
  )

  return (
    <Group bg='yellow' px={SizePrimitives[2]} py={SizePrimitives[1.5]}>
      <Text c='white'>{t('Impersonating {{name}}', { name: user ? getName(user) : impersonating })}</Text>
      <TButton
        variant='secondary'
        tooltip={`${t('Stop Impersonating')} (${isMac ? '⌘ + Shift + I' : 'Ctrl + Alt + I'})`}
        aria-keyshortcuts={isMac ? 'Meta+Shift+i' : 'Control+Alt+i'}
        onClick={stopImpersonating}
      >
        {t('Stop Impersonating')}
      </TButton>
    </Group>
  )
}
